import { useTranslation } from 'react-i18next';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, Globe, Heart, Mail, Menu, X, ArrowRight, Share2, AlertTriangle, Lightbulb, Info, Link2, Check, List, ChevronDown, Sparkles, Quote } from 'lucide-react';
import { SignInButton, UserButton, useAuth } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import { Button, LanguageModal } from '@/components/ui';
import { getPostByLang, getAllPostsByLang, getAvailableLangs, defaultAuthor, SUPPORTED_LANGS, type BlogLang, type LocalizedPost } from '@/lib/blogPosts';
import { useState, useEffect, useRef, useMemo } from 'react';

// Extract headings from markdown for TOC
function extractHeadings(md: string): { id: string; text: string; level: number }[] {
  return md.split('\n').reduce<{ id: string; text: string; level: number }[]>((acc, line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('### ')) {
      const text = trimmed.slice(4);
      const id = text.toLowerCase().replace(/[^a-zčćžšđ0-9]+/gi, '-').replace(/(^-|-$)/g, '');
      acc.push({ id, text, level: 3 });
    } else if (trimmed.startsWith('## ')) {
      const text = trimmed.slice(3);
      const id = text.toLowerCase().replace(/[^a-zčćžšđ0-9]+/gi, '-').replace(/(^-|-$)/g, '');
      acc.push({ id, text, level: 2 });
    }
    return acc;
  }, []);
}

interface BlogPostPageProps {
  /** App.tsx pins the language when the route is /{lang}/blog/:slug. */
  langOverride?: BlogLang;
}

const localeMap: Record<BlogLang, string> = {
  hr: 'hr-HR', en: 'en-US', sl: 'sl-SI', de: 'de-DE', it: 'it-IT',
};

export function BlogPostPage({ langOverride }: BlogPostPageProps = {}) {
  const { slug } = useParams<{ slug: string }>();
  const { i18n } = useTranslation();
  const { isSignedIn } = useAuth();
  // i18n only knows hr/en; for sl/de/it we still pin lang from the route.
  const lang: BlogLang = langOverride ?? (
    SUPPORTED_LANGS.includes(i18n.language as BlogLang) ? (i18n.language as BlogLang) : 'hr'
  );

  // Only sync i18n for hr/en; we don't have sl/de/it bundles loaded.
  useEffect(() => {
    if (langOverride && (langOverride === 'hr' || langOverride === 'en') && i18n.language !== langOverride) {
      i18n.changeLanguage(langOverride);
    }
  }, [langOverride, i18n]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [activeHeadingId, setActiveHeadingId] = useState('');
  const [copied, setCopied] = useState(false);
  const articleRef = useRef<HTMLElement>(null);

  // Active-language post; falls back to HR if a translation isn't yet generated.
  const post: LocalizedPost | undefined =
    (slug ? getPostByLang(slug, lang) : undefined) ??
    (slug ? getPostByLang(slug, 'hr') : undefined);

  // Scroll tracking: header background + reading progress
  useEffect(() => {
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > 20);
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setReadProgress(docHeight > 0 ? Math.min((window.scrollY / docHeight) * 100, 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const content = post?.content ?? '';
  const headings = useMemo(() => extractHeadings(content), [content]);

  // IntersectionObserver for active heading tracking
  useEffect(() => {
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActiveHeadingId(e.target.id); }); },
      { rootMargin: '-80px 0px -75% 0px', threshold: 0 }
    );
    const timer = setTimeout(() => {
      headings.forEach(({ id }) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    }, 300);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [headings]);

  if (!post) return <Navigate to={`/${lang}/blog/`} replace />;

  const [langOpen, setLangOpen] = useState(false);

  // Selecting a language navigates to the sibling post URL — same pattern as
  // BlogPage, but we keep the slug so the visitor stays on the same article.
  const handleLangSelect = (target: string) => {
    if (!SUPPORTED_LANGS.includes(target as BlogLang)) target = 'hr';
    if (target === 'hr' || target === 'en') i18n.changeLanguage(target);
    window.location.href = `/${target}/blog/${post.slug}/`;
  };

  const title = post.title;
  const description = post.description;
  const authorRole = post.author.role;
  const authorBio = post.author.bio || (lang === 'en' ? defaultAuthor.bioEn : defaultAuthor.bio);
  // Trailing slash matters here: Cloudflare Pages serves pretty URLs by
  // 308-redirecting /path → /path/ before _redirects rules can intervene.
  // Advertising the no-slash form would force every crawler hop through a
  // redirect and let Search Console drift between canonicalisation states.
  // The with-slash form matches what Googlebot actually settles on.
  const canonicalUrl = `https://bookiapp.com/${lang}/blog/${post.slug}/`;
  const availableLangs = slug ? getAvailableLangs(slug) : [];

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Structured Data ──
  // Build absolute image URLs for schema (Google requires absolute URLs).
  const absUrl = (src: string) => src.startsWith('http') ? src : `https://bookiapp.com${src}`;
  const allImages = [post.coverImage, ...(post.inlineImages || [])].filter(Boolean).map(absUrl);

  // Author is the BookiApp brand, not a named individual. We emit an
  // Organization schema rather than a Person schema — Google's 2026 EEAT
  // signals reward verifiable authorship, and an unverifiable persona
  // ("Mario B." with no LinkedIn / sameAs anywhere on the open web) is a
  // weaker signal than a clearly-disclosed organisational author tied to
  // a real SaaS product, marketing site, pricing page, etc.
  const knowsAboutByLang: Record<BlogLang, string[]> = {
    hr: ['iznajmljivanje apartmana', 'turizam Hrvatska', 'Booking.com', 'Airbnb', 'guest experience', 'short-term rental'],
    en: ['vacation rental hosting', 'Croatia tourism', 'Booking.com', 'Airbnb', 'guest experience', 'short-term rental'],
    sl: ['oddajanje apartmajev', 'turizem Hrvaška', 'Booking.com', 'Airbnb', 'izkušnja gostov'],
    de: ['Ferienwohnung vermieten', 'Tourismus Kroatien', 'Booking.com', 'Airbnb', 'Gästeerlebnis'],
    it: ['affitto appartamenti vacanze', 'turismo Croazia', 'Booking.com', 'Airbnb', 'esperienza ospiti'],
  };
  const authorSchema = {
    '@type': 'Organization',
    name: post.author.name,                       // "BookiApp Tim" / "BookiApp Team" etc.
    description: post.author.role,
    url: 'https://bookiapp.com',
    knowsAbout: knowsAboutByLang[lang],
    parentOrganization: { '@type': 'Organization', name: 'BookiApp', url: 'https://bookiapp.com' },
  };

  // Article-level entities (`about`/`mentions`) help generative engines
  // map this page into their topic graph for retrieval.
  const fallbackAboutByLang: Record<BlogLang, string[]> = {
    hr: ['Iznajmljivanje apartmana u Hrvatskoj', post.category],
    en: ['Vacation rental hosting in Croatia', post.category],
    sl: ['Oddajanje apartmajev na Hrvaškem', post.category],
    de: ['Ferienwohnung vermieten in Kroatien', post.category],
    it: ['Affitto appartamenti vacanze in Croazia', post.category],
  };
  const aboutEntities = post.about && post.about.length > 0 ? post.about : fallbackAboutByLang[lang];

  const keywordsValue = post.keywords && post.keywords.length > 0
    ? post.keywords.join(', ')
    : post.category;

  // dateModified prefers lastReviewed when present so freshness sweeps can
  // bump it without touching original publish date (which is stable).
  const lastModified = post.lastReviewed || post.date;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    image: allImages,
    author: authorSchema,
    publisher: {
      '@type': 'Organization',
      name: 'BookiApp',
      url: 'https://bookiapp.com',
      logo: { '@type': 'ImageObject', url: 'https://bookiapp.com/favicon-original.png', width: 512, height: 512 },
    },
    datePublished: post.date,
    dateModified: lastModified,
    inLanguage: localeMap[lang],
    isAccessibleForFree: true,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    url: canonicalUrl,
    wordCount: content.split(/\s+/).length,
    articleSection: post.category,
    keywords: keywordsValue,
    about: aboutEntities.map(name => ({ '@type': 'Thing', name })),
    // Speakable highlights the TL;DR for voice/AI surfacing.
    speakable: { '@type': 'SpeakableSpecification', cssSelector: ['[data-speakable]'] },
  };

  const blogIndexLabel: Record<BlogLang, string> = {
    hr: 'Savjeti', en: 'Tips', sl: 'Nasveti', de: 'Tipps', it: 'Consigli',
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'BookiApp', item: 'https://bookiapp.com' },
      { '@type': 'ListItem', position: 2, name: blogIndexLabel[lang], item: `https://bookiapp.com/${lang}/blog/` },
      { '@type': 'ListItem', position: 3, name: title, item: canonicalUrl },
    ],
  };

  // FAQPage schema is back. Google's March 2026 rich-result restriction only
  // affects the *visual* FAQ rich snippet on SERP — we lose the dropdown
  // accordion in Google but generative engines (ChatGPT, Perplexity, Claude,
  // Gemini) still parse FAQPage JSON-LD as ranked Q&A pairs and cite them
  // disproportionately. AEO upside > lost rich-result downside for us.
  const faqSchema = post.faq.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faq.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  } : null;

  // HowTo schema is emitted when isHowTo is set OR when the article markdown
  // contains an obvious step list (3+ ordered list items). Google AI Overviews
  // explicitly prefer HowTo-marked content for "kako" / "how to" queries.
  const stepsForHowTo = useMemo(() => {
    if (!post.isHowTo) return [];
    // Pull all `1. `, `2. ` etc lines out of markdown in document order.
    const lines = content.split('\n').map(l => l.trim());
    return lines
      .filter(l => /^\d+[.)]\s/.test(l))
      .map((l, idx) => {
        const text = l.replace(/^\d+[.)]\s/, '').replace(/\*\*/g, '');
        return { '@type': 'HowToStep', position: idx + 1, name: text.split(/[.:]/)[0].slice(0, 80), text };
      });
  }, [content, post.isHowTo]);
  const howToSchema = post.isHowTo && stepsForHowTo.length >= 2 ? {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: title,
    description,
    image: allImages[0],
    totalTime: `PT${Math.max(post.readTime, 5)}M`,
    step: stepsForHowTo,
  } : null;

  // Inject `citation` array onto the article schema when sources are declared.
  // schema.org/Article supports `citation` as a list of CreativeWork — gives
  // AI engines a verifiable provenance trail for every numeric claim.
  if (post.sources && post.sources.length > 0) {
    (articleSchema as Record<string, unknown>).citation = post.sources.map(s => ({
      '@type': 'CreativeWork',
      name: s.label,
      url: s.url,
    }));
  }

  // ── Inline formatting: links, bold, italic, code, footnotes ──
  // Link regex restricts label to non-]/[/newline chars and URL to non-)/whitespace.
  // Without these guards the previous /\[(.+?)\]\((.+?)\)/ would happily span
  // unrelated brackets — e.g. a placeholder like "[Ime gosta]" followed paragraphs
  // later by "[eVisitor](https://...)" got swallowed into one giant link, turning
  // the whole blockquote into hyperlinked text.
  //
  // Footnote markers `[^N]` render as superscript numbered links into the Sources
  // list at the bottom — this is what gets ChatGPT/Perplexity to actually cite us
  // (they parse footnoted articles as "well-cited" and rank them higher).
  const inlineFormat = (text: string) => {
    // Defensive: callers occasionally pass undefined/null when a markdown
    // block split yields an empty cell (tables with extra separators) or
    // when a custom block has no inner body. React 19's SSR is strict
    // enough that throwing here aborts the whole route render.
    if (typeof text !== 'string') return '';
    return text
      .replace(/\[\^(\d+)\]/g, '<sup><a href="#source-$1" class="text-cyan-600 hover:text-cyan-700 font-semibold no-underline px-1">[$1]</a></sup>')
      .replace(/\[([^\]\n[]+)\]\(([^()\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyan-600 hover:text-cyan-700 underline decoration-cyan-500/30 underline-offset-2 transition-colors">$1</a>')
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-[hsl(var(--foreground))] font-bold">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-sm font-mono">$1</code>');
  };

  // ── Markdown renderer ──
  // h2Counter is closed over the renderer call so we can number sections
  // ("01", "02", "03") without polluting the data model. Reset on every render.
  const renderMarkdown = (md: string) => {
    let h2Counter = 0;
    // Number ordered lists across the whole article so step references survive
    // even when an image splits the list into two paragraphs.
    let olCounter = 0;
    return md.split('\n\n').map((block, i) => {
      const trimmed = block.trim();
      if (!trimmed) return null;

      // Horizontal rule → gradient divider
      if (trimmed === '---') return (
        <div key={i} className="my-10 flex items-center gap-4">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
          <div className="w-1.5 h-1.5 rounded-full bg-cyan-500/40" />
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
        </div>
      );

      // ─── :::stats block — 2-4 stat cards in a grid ───
      // Syntax:
      //   :::stats
      //   70% | zahtjeva uspješno
      //   72h | prosječno rješavanje
      //   40% | veća šansa u 2. fazi
      //   :::
      // Pulls buried numbers out of paragraphs and into something AI engines
      // and skimmers both reach for first.
      if (trimmed.startsWith(':::stats')) {
        const lines = trimmed.split('\n').slice(1, -1).filter(l => l.trim() && l.includes('|'));
        const stats = lines.map(l => {
          const [value, ...labelParts] = l.split('|').map(s => s.trim());
          return { value, label: labelParts.join('|').trim() };
        });
        const cols = stats.length === 2 ? 'grid-cols-2' : stats.length === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4';
        return (
          <div key={i} className={`grid ${cols} gap-3 md:gap-4 my-10`}>
            {stats.map((s, j) => (
              <div key={j} className="bg-gradient-to-br from-cyan-500/5 to-blue-500/5 border border-cyan-500/15 rounded-2xl p-5 md:p-6 text-center">
                <p className="text-3xl md:text-4xl font-extrabold bg-gradient-to-br from-cyan-600 to-blue-600 bg-clip-text text-transparent leading-none mb-2">{s.value}</p>
                <p className="text-xs md:text-sm text-[hsl(var(--muted-foreground))] leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        );
      }

      // ─── :::quote block — pull quote ───
      // Syntax:
      //   :::quote
      //   Najjači dokaz je dokumentirana komunikacija s gostom preko platforme.
      //   — BookiApp Tim
      //   :::
      // Big italicised display quote. Different from a > blockquote (which is
      // for citing someone else's words) — this is for highlighting our own
      // most quotable line so social shares pick it up.
      if (trimmed.startsWith(':::quote')) {
        const inner = trimmed.split('\n').slice(1, -1).join('\n').trim();
        const parts = inner.split(/\n[—–-]\s*/);
        const body = parts[0];
        const attribution = parts[1];
        return (
          <figure key={i} className="my-12 md:my-14 px-4 md:px-8 text-center">
            <Quote className="h-10 w-10 mx-auto mb-4 text-cyan-500/30" strokeWidth={2.5} />
            <blockquote className="text-[1.375rem] sm:text-2xl lg:text-3xl font-semibold leading-snug text-[hsl(var(--foreground))] tracking-tight" dangerouslySetInnerHTML={{ __html: inlineFormat(body) }} />
            {attribution && (
              <figcaption className="mt-4 text-sm text-[hsl(var(--muted-foreground))] font-medium">— {attribution}</figcaption>
            )}
          </figure>
        );
      }

      // ─── :::insight block — branded data callout ───
      // Syntax:
      //   :::insight
      //   Iz analize 200+ iznajmljivača u BookiApp bazi, prosječni response time je 47 minuta.
      //   :::
      // Wraps any "from our data" claim in a visible badge so AI engines
      // recognise it as primary research, not unsourced opinion.
      if (trimmed.startsWith(':::insight')) {
        const inner = trimmed.split('\n').slice(1, -1).join(' ').trim();
        return (
          <div key={i} className="my-10 rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/8 to-blue-500/5 p-5 md:p-6 flex gap-4 shadow-sm shadow-cyan-500/5">
            <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] md:text-xs font-bold text-cyan-600 uppercase tracking-[0.15em] mb-1.5">BookiApp Data</p>
              <p className="text-base md:text-lg text-[hsl(var(--foreground))] leading-relaxed font-medium" dangerouslySetInnerHTML={{ __html: inlineFormat(inner) }} />
            </div>
          </div>
        );
      }

      // Table → rounded, alternating rows
      if (trimmed.includes('|') && trimmed.split('\n').length >= 2) {
        const lines = trimmed.split('\n').filter(l => l.trim());
        const isTable = lines.length >= 2 && lines[1].includes('---');
        if (isTable) {
          const headers = lines[0].split('|').map(c => c.trim()).filter(Boolean);
          const rows = lines.slice(2).map(l => l.split('|').map(c => c.trim()).filter(Boolean));
          return (
            <div key={i} className="overflow-x-auto my-10 rounded-xl border border-[hsl(var(--border))]/50 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[hsl(var(--muted))]/50">
                    {headers.map((h, j) => <th key={j} className="text-left p-3 md:p-4 font-semibold text-[hsl(var(--foreground))]" dangerouslySetInnerHTML={{ __html: inlineFormat(h) }} />)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, j) => (
                    <tr key={j} className={`border-t border-[hsl(var(--border))]/30 ${j % 2 === 1 ? 'bg-[hsl(var(--muted))]/20' : ''}`}>
                      {row.map((cell, k) => <td key={k} className="p-3 md:p-4 text-[hsl(var(--muted-foreground))]" dangerouslySetInnerHTML={{ __html: inlineFormat(cell) }} />)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
      }

      // Image — supports `![alt|caption](src)` for visible figcaptions.
      // Captions add context (what is the reader looking at? why is it here?)
      // and bump dwell time vs. a bare floating image.
      if (trimmed.startsWith('![')) {
        const match = trimmed.match(/!\[(.+?)\]\((.+?)\)/);
        if (match) {
          const [, altRaw, src] = match;
          const [alt, caption] = altRaw.includes('|') ? altRaw.split('|').map(s => s.trim()) : [altRaw, ''];
          return (
            <figure key={i} className="my-10 md:my-12">
              <div className="overflow-hidden rounded-2xl">
                <img src={src} alt={alt} className="w-full rounded-2xl shadow-lg hover:scale-[1.02] transition-transform duration-500" loading="lazy" />
              </div>
              {caption && (
                <figcaption className="mt-3 text-center text-xs md:text-sm text-[hsl(var(--muted-foreground))] italic px-4">
                  {caption}
                </figcaption>
              )}
            </figure>
          );
        }
      }

      // Heading ## → with gradient accent bar + numbered prefix + id
      if (trimmed.startsWith('## ') && !trimmed.startsWith('### ')) {
        const text = trimmed.slice(3);
        const id = text.toLowerCase().replace(/[^a-zčćžšđ0-9]+/gi, '-').replace(/(^-|-$)/g, '');
        h2Counter += 1;
        const num = String(h2Counter).padStart(2, '0');
        return (
          <h2 key={i} id={id} className="text-[1.75rem] sm:text-3xl md:text-[2.125rem] lg:text-[2.25rem] font-extrabold mt-16 md:mt-24 mb-5 md:mb-6 scroll-mt-24 tracking-tight leading-[1.15]">
            <span className="block text-[11px] md:text-xs font-bold text-cyan-600 uppercase tracking-[0.2em] mb-2">— {num}</span>
            <span className="flex items-start gap-3 leading-[1.15]">
              <span className="w-1 h-8 md:h-9 mt-1.5 md:mt-2 bg-gradient-to-b from-cyan-500 to-blue-600 rounded-full shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: inlineFormat(text) }} />
            </span>
          </h2>
        );
      }
      if (trimmed.startsWith('### ')) {
        const text = trimmed.slice(4);
        const id = text.toLowerCase().replace(/[^a-zčćžšđ0-9]+/gi, '-').replace(/(^-|-$)/g, '');
        return <h3 key={i} id={id} className="text-[1.375rem] sm:text-2xl md:text-2xl font-bold mt-10 md:mt-12 mb-3 md:mb-4 scroll-mt-24 tracking-tight leading-snug" dangerouslySetInnerHTML={{ __html: inlineFormat(text) }} />;
      }

      // Callout auto-detection: **Greška/Tip/Važno/Savjet/Napomena/Mistake/Important/Note/Warning ...:**
      const calloutMatch = trimmed.match(/^\*\*([^:*]+?):\*\*\s*([\s\S]*)/);
      if (calloutMatch) {
        const keyword = calloutMatch[1].toLowerCase();
        const isWarning = /greška|mistake|warning|važno|important/i.test(keyword);
        const isTip = /tip|savjet|note|napomena|trik|trick|moj trik/i.test(keyword);
        if (isWarning || isTip || /digitalna|investicija|praktično|umjesto zaključka|praktical|instead/i.test(keyword)) {
          const body = calloutMatch[2];
          const colors = isWarning
            ? { bg: 'bg-amber-500/5', border: 'border-amber-500/20', icon: 'text-amber-500', label: 'text-amber-600' }
            : isTip
            ? { bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', icon: 'text-emerald-500', label: 'text-emerald-600' }
            : { bg: 'bg-blue-500/5', border: 'border-blue-500/20', icon: 'text-blue-500', label: 'text-blue-600' };
          const IconComp = isWarning ? AlertTriangle : isTip ? Lightbulb : Info;
          return (
            <div key={i} className={`my-8 rounded-xl border p-4 md:p-5 flex gap-3 ${colors.bg} ${colors.border}`}>
              <IconComp className={`h-5 w-5 shrink-0 mt-0.5 ${colors.icon}`} />
              <div>
                <p className={`text-xs font-bold uppercase tracking-wider mb-1.5 ${colors.label}`}>{calloutMatch[1]}</p>
                <p className="text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed" dangerouslySetInnerHTML={{ __html: inlineFormat(body) }} />
              </div>
            </div>
          );
        }
      }

      // Blockquote
      if (trimmed.startsWith('> ')) {
        const quoteText = trimmed.split('\n').map(l => l.replace(/^>\s?/, '')).join(' ');
        return (
          <blockquote key={i} className="my-8 pl-5 border-l-4 border-cyan-500/40 py-2">
            <p className="text-lg md:text-xl italic text-[hsl(var(--foreground))]/80 leading-relaxed" dangerouslySetInnerHTML={{ __html: inlineFormat(quoteText) }} />
          </blockquote>
        );
      }

      // Unordered list — accepts `- `, `* `, or `*   ` (Gemini sometimes
      // emits the asterisk-with-spaces variant which the previous renderer
      // dropped silently into a paragraph, producing the "wall of text" look).
      if (/^[*-]\s+/.test(trimmed)) {
        const items = trimmed.split('\n').filter(l => /^[*-]\s+/.test(l));
        if (items.length > 0) {
          return (
            <ul key={i} className="space-y-3 my-7">
              {items.map((item, j) => (
                <li key={j} className="flex gap-3 text-[hsl(var(--muted-foreground))]">
                  <span className="mt-[0.7rem] w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0" />
                  <span className="text-base md:text-lg leading-[1.75]" dangerouslySetInnerHTML={{ __html: inlineFormat(item.replace(/^[*-]\s+/, '')) }} />
                </li>
              ))}
            </ul>
          );
        }
      }

      // Ordered list — supports `1. `, `1.  ` and `1)  `. Counter is
      // article-scoped so split lists (image between steps) keep numbering.
      if (/^\d+[.)]\s/.test(trimmed)) {
        const items = trimmed.split('\n').filter(l => /^\d+[.)]\s/.test(l));
        if (items.length > 0) {
          return (
            <ol key={i} className="space-y-4 my-7">
              {items.map((item, j) => {
                olCounter += 1;
                return (
                  <li key={j} className="flex gap-3 text-[hsl(var(--muted-foreground))]">
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-xs font-bold shrink-0 mt-0.5 shadow-sm">{olCounter}</span>
                    <span className="text-base md:text-lg leading-[1.75] pt-0.5" dangerouslySetInnerHTML={{ __html: inlineFormat(item.replace(/^\d+[.)]\s/, '')) }} />
                  </li>
                );
              })}
            </ol>
          );
        }
      }

      // Paragraph — body copy uses `text-lg md:text-xl` (~19-20px), the 2026
      // long-form standard. Substack/Medium use the same range.
      return <p key={i} className="text-lg md:text-xl text-[hsl(var(--muted-foreground))] leading-[1.8] my-6" dangerouslySetInnerHTML={{ __html: inlineFormat(trimmed) }} />;
    }).filter(Boolean);
  };

  // Related = other posts in this language (latest first), excluding self.
  const relatedPosts = getAllPostsByLang(lang).filter((p) => p.slug !== post.slug).slice(0, 2);

  const t = {
    hr: {
      back: 'Natrag na savjete', share: 'Podijeli', signIn: 'Prijava', dashboard: 'Dashboard', pricing: 'Cijene',
      about: 'O nama', contact: 'Kontakt', cta: 'Započni sada', minRead: 'min čitanja',
      relatedTitle: 'Povezani članci', readMore: 'Čitaj više', navBlog: 'Savjeti',
      ctaTitle: 'Spremni automatizirati komunikaciju s gostima?',
      ctaSubtitle: 'Isprobajte BookiApp besplatno i uštedite sate svaki tjedan.',
      ctaButton: 'Započni sada',
      tldrLabel: 'Ukratko', faqTitle: 'Često postavljana pitanja',
      footerDesc: 'Sve o boravku — u jednoj aplikaciji za vaše goste.',
      legal: 'Pravno', privacy: 'Privatnost', terms: 'Uvjeti korištenja', cookies: 'Kolačići',
      company: 'Tvrtka', madeIn: 'Napravljeno s', inCroatia: 'u Hrvatskoj', rights: 'Sva prava pridržana.',
      toc: 'Sadržaj članka', contents: 'Sadržaj', minLeft: 'min preostalo', copyLink: 'Kopiraj link',
      keyTakeaways: 'Ključne stvari', sources: 'Izvori', updated: 'Ažurirano',
    },
    en: {
      back: 'Back to tips', share: 'Share', signIn: 'Sign In', dashboard: 'Dashboard', pricing: 'Pricing',
      about: 'About', contact: 'Contact', cta: 'Get started', minRead: 'min read',
      relatedTitle: 'Related articles', readMore: 'Read more', navBlog: 'Tips',
      ctaTitle: 'Ready to automate guest communication?',
      ctaSubtitle: 'Try BookiApp free and save hours every week.',
      ctaButton: 'Get started',
      tldrLabel: 'Key takeaway', faqTitle: 'Frequently asked questions',
      footerDesc: 'Guest Experience platform for modern hosts.',
      legal: 'Legal', privacy: 'Privacy', terms: 'Terms of Service', cookies: 'Cookies',
      company: 'Company', madeIn: 'Made with', inCroatia: 'in Croatia', rights: 'All rights reserved.',
      toc: 'Table of contents', contents: 'Contents', minLeft: 'min left', copyLink: 'Copy link',
      keyTakeaways: 'Key points', sources: 'Sources', updated: 'Updated',
    },
    sl: {
      back: 'Nazaj na nasvete', share: 'Deli', signIn: 'Prijava', dashboard: 'Nadzorna plošča', pricing: 'Cene',
      about: 'O nas', contact: 'Kontakt', cta: 'Začni zdaj', minRead: 'min branja',
      relatedTitle: 'Povezani članki', readMore: 'Preberi več', navBlog: 'Nasveti',
      ctaTitle: 'Pripravljeni avtomatizirati komunikacijo z gosti?',
      ctaSubtitle: 'Preizkusite BookiApp brezplačno in prihranite ure vsak teden.',
      ctaButton: 'Začni zdaj',
      tldrLabel: 'Na kratko', faqTitle: 'Pogosta vprašanja',
      footerDesc: 'Vse o bivanju — v eni aplikaciji za vaše goste.',
      legal: 'Pravno', privacy: 'Zasebnost', terms: 'Pogoji uporabe', cookies: 'Piškotki',
      company: 'Podjetje', madeIn: 'Narejeno s', inCroatia: 'na Hrvaškem', rights: 'Vse pravice pridržane.',
      toc: 'Vsebina članka', contents: 'Vsebina', minLeft: 'min preostalo', copyLink: 'Kopiraj povezavo',
      keyTakeaways: 'Ključne točke', sources: 'Viri', updated: 'Posodobljeno',
    },
    de: {
      back: 'Zurück zu Tipps', share: 'Teilen', signIn: 'Anmelden', dashboard: 'Dashboard', pricing: 'Preise',
      about: 'Über uns', contact: 'Kontakt', cta: 'Jetzt starten', minRead: 'Min. Lesezeit',
      relatedTitle: 'Verwandte Artikel', readMore: 'Weiterlesen', navBlog: 'Tipps',
      ctaTitle: 'Bereit, die Gästekommunikation zu automatisieren?',
      ctaSubtitle: 'Testen Sie BookiApp kostenlos und sparen Sie jede Woche Stunden.',
      ctaButton: 'Jetzt starten',
      tldrLabel: 'Kurzfassung', faqTitle: 'Häufig gestellte Fragen',
      footerDesc: 'Alles zum Aufenthalt — in einer App für Ihre Gäste.',
      legal: 'Rechtliches', privacy: 'Datenschutz', terms: 'AGB', cookies: 'Cookies',
      company: 'Unternehmen', madeIn: 'Gemacht mit', inCroatia: 'in Kroatien', rights: 'Alle Rechte vorbehalten.',
      toc: 'Inhaltsverzeichnis', contents: 'Inhalt', minLeft: 'Min. übrig', copyLink: 'Link kopieren',
      keyTakeaways: 'Kernpunkte', sources: 'Quellen', updated: 'Aktualisiert',
    },
    it: {
      back: 'Torna ai consigli', share: 'Condividi', signIn: 'Accedi', dashboard: 'Dashboard', pricing: 'Prezzi',
      about: 'Chi siamo', contact: 'Contatti', cta: 'Inizia ora', minRead: 'min di lettura',
      relatedTitle: 'Articoli correlati', readMore: 'Leggi di più', navBlog: 'Consigli',
      ctaTitle: 'Pronto ad automatizzare la comunicazione con gli ospiti?',
      ctaSubtitle: 'Prova BookiApp gratis e risparmia ore ogni settimana.',
      ctaButton: 'Inizia ora',
      tldrLabel: 'In sintesi', faqTitle: 'Domande frequenti',
      footerDesc: "Tutto sul soggiorno — in un'unica app per i tuoi ospiti.",
      legal: 'Legale', privacy: 'Privacy', terms: 'Termini', cookies: 'Cookie',
      company: 'Azienda', madeIn: 'Realizzato con', inCroatia: 'in Croazia', rights: 'Tutti i diritti riservati.',
      toc: 'Indice dei contenuti', contents: 'Contenuti', minLeft: 'min rimanenti', copyLink: 'Copia link',
      keyTakeaways: 'Punti chiave', sources: 'Fonti', updated: 'Aggiornato',
    },
  }[lang];

  const h2Headings = headings.filter(h => h.level === 2);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-[3px] bg-transparent pointer-events-none">
        <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-[width] duration-150 ease-out" style={{ width: `${readProgress}%` }} />
      </div>

      <Helmet>
        <html lang={lang} />
        <title>{title} | BookiApp</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="author" content={post.author.name} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content={localeMap[lang].replace('-', '_')} />
        {availableLangs.filter((l) => l !== lang).map((l) => (
          <meta key={l} property="og:locale:alternate" content={localeMap[l].replace('-', '_')} />
        ))}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="BookiApp" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author.name} />
        <meta property="article:section" content={post.category} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {/* hreflang — only emit alternates for languages we actually have a
            translation for. x-default points at HR (primary launch market). */}
        {availableLangs.map((l) => (
          <link key={l} rel="alternate" hrefLang={l} href={`https://bookiapp.com/${l}/blog/${post.slug}/`} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`https://bookiapp.com/hr/blog/${post.slug}/`} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
        {howToSchema && <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>}
      </Helmet>

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerScrolled ? 'bg-[hsl(var(--background))]/70 backdrop-blur-2xl border-b border-[hsl(var(--border))]/50 shadow-lg shadow-black/[0.03]' : 'bg-transparent'}`}>
        <div className="container mx-auto flex items-center justify-between px-4 py-3 md:py-4">
          <Link to="/" className="flex items-center group shrink-0">
            <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto md:h-10 group-hover:scale-105 transition-transform duration-300" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {[{ to: '/pricing/', label: t.pricing }, { to: `/${lang}/blog/`, label: t.navBlog }, { to: '/about/', label: t.about }, { to: '/contact/', label: t.contact }].map(l => (
              <Link key={l.to} to={l.to} className={`text-sm font-medium transition-colors relative group ${l.to.endsWith('/blog/') ? 'text-[hsl(var(--foreground))]' : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'}`}>
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 group-hover:w-full transition-all duration-300" />
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLangOpen(true)}
              className="px-2 md:px-3 hover:bg-cyan-500/10"
              aria-haspopup="dialog"
              aria-expanded={langOpen}
            >
              <Globe className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">{lang.toUpperCase()}</span>
            </Button>
            {!isSignedIn && (
              <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
                <Button variant="ghost" size="sm" className="hidden sm:flex hover:bg-cyan-500/10">{t.signIn}</Button>
              </SignInButton>
            )}
            <Link to={isSignedIn ? '/dashboard' : '/pricing'} className="hidden sm:block">
              <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0 text-xs md:text-sm shadow-lg shadow-cyan-500/20">{isSignedIn ? t.dashboard : t.cta}</Button>
            </Link>
            {isSignedIn && (
              <UserButton
                afterSignOutUrl="/"
                appearance={{ elements: { avatarBox: 'h-9 w-9 rounded-xl ring-2 ring-transparent hover:ring-cyan-200 transition-all duration-200' } }}
              />
            )}
            <Button variant="ghost" size="sm" className="md:hidden px-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Sticky Share Bar - Desktop */}
      <div className="hidden lg:flex fixed left-[max(1rem,calc((100vw-76rem)/2-3rem))] top-1/2 -translate-y-1/2 flex-col gap-2.5 z-40">
        <button onClick={() => { if (navigator.share) navigator.share({ title, url: canonicalUrl }); else handleCopy(); }}
          className="w-10 h-10 rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))]/50 flex items-center justify-center hover:border-cyan-500/50 hover:text-cyan-600 transition-all shadow-sm" title={t.share}>
          <Share2 className="h-4 w-4" />
        </button>
        <button onClick={handleCopy}
          className="w-10 h-10 rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))]/50 flex items-center justify-center hover:border-cyan-500/50 hover:text-cyan-600 transition-all shadow-sm" title={t.copyLink}>
          {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Link2 className="h-4 w-4" />}
        </button>
        <a href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(canonicalUrl)}`}
          className="w-10 h-10 rounded-full bg-[hsl(var(--card))] border border-[hsl(var(--border))]/50 flex items-center justify-center hover:border-cyan-500/50 hover:text-cyan-600 transition-all shadow-sm" title="Email">
          <Mail className="h-4 w-4" />
        </a>
      </div>

      {/* Article */}
      <article ref={articleRef} className="pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto flex gap-10">
            {/* Main Content Column */}
            <div className="max-w-3xl flex-1 min-w-0 mx-auto xl:mx-0">
              {/* Breadcrumb */}
              <Link to={`/${lang}/blog/`} className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-cyan-600 transition-colors mb-6 md:mb-8">
                <ArrowLeft className="h-4 w-4" /> {t.back}
              </Link>

              {/* Cover Image — smaller (h-56/72 vs old h-64/96) so the
                  Title + Key Takeaways are above the fold even on laptops. */}
              <div className="relative h-56 md:h-72 rounded-2xl overflow-hidden mb-7 group">
                <img src={post.coverImage} alt={post.coverImageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>

              {/* Meta — adds visible "Updated" stamp when lastReviewed differs
                  from publish date. Both AI engines and humans treat freshness
                  as a ranking signal in 2026. */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  post.categoryColor === 'cyan' ? 'bg-cyan-500/10 text-cyan-600' :
                  post.categoryColor === 'emerald' ? 'bg-emerald-500/10 text-emerald-600' :
                  post.categoryColor === 'amber' ? 'bg-amber-500/10 text-amber-600' :
                  post.categoryColor === 'violet' ? 'bg-violet-500/10 text-violet-600' :
                  post.categoryColor === 'red' ? 'bg-red-500/10 text-red-600' :
                  post.categoryColor === 'blue' ? 'bg-blue-500/10 text-blue-600' :
                  'bg-rose-500/10 text-rose-600'
                }`}>
                  {post.category}
                </span>
                <div className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]">
                  <Calendar className="h-3 w-3" />
                  <span>{new Date(post.date).toLocaleDateString(localeMap[lang], { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
                {post.lastReviewed && post.lastReviewed !== post.date && (
                  <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                    <Sparkles className="h-3 w-3" />
                    <span>{t.updated} {new Date(post.lastReviewed).toLocaleDateString(localeMap[lang], { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  </div>
                )}
                <div className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]">
                  <Clock className="h-3 w-3" />
                  <span>{post.readTime} {t.minRead}</span>
                </div>
              </div>

              {/* Title — mobile bumped from text-3xl (30px) → text-[2.125rem]
                  (34px) so long Croatian titles read with proper editorial
                  weight on phones (most of our blog traffic). */}
              <h1 className="text-[2.125rem] sm:text-4xl md:text-[2.75rem] lg:text-5xl font-extrabold tracking-tight mb-6 md:mb-10 leading-[1.1]">{title}</h1>

              {/* Author + Share */}
              <div className="flex items-center justify-between gap-4 mb-8 pb-8 border-b border-[hsl(var(--border))]/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold shrink-0">{post.author.avatar}</div>
                  <div>
                    <p className="text-sm font-semibold">{post.author.name}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">{authorRole}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-xl gap-2 text-xs shrink-0" onClick={() => { if (navigator.share) { navigator.share({ title, url: window.location.href }); } else handleCopy(); }}>
                  <Share2 className="h-3.5 w-3.5" /> {t.share}
                </Button>
              </div>

              {/* TL;DR — marked speakable for AI/voice extraction (matches schema cssSelector). */}
              {post.tldr && (
                <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-xl p-5 md:p-6 mb-6" data-speakable>
                  <p className="text-xs font-bold text-cyan-600 uppercase tracking-wider mb-2">{t.tldrLabel}</p>
                  <p className="text-base md:text-lg text-[hsl(var(--foreground))] leading-relaxed">{post.tldr}</p>
                </div>
              )}

              {/* Key Takeaways — 3-5 bullet points right above the fold.
                  This is where ChatGPT/Perplexity/Claude actually grab citations
                  from when they answer "what does X article say?". Lists win. */}
              {post.keyTakeaways && post.keyTakeaways.length > 0 && (
                <div className="bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 border border-emerald-500/15 rounded-2xl p-5 md:p-6 mb-8">
                  <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5" /> {t.keyTakeaways}
                  </p>
                  <ul className="space-y-2.5">
                    {post.keyTakeaways.map((kt, i) => (
                      <li key={i} className="flex gap-3 text-[hsl(var(--foreground))]">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/15 text-emerald-600 text-[11px] font-bold shrink-0 mt-1">{i + 1}</span>
                        <span className="text-base md:text-lg leading-snug" dangerouslySetInnerHTML={{ __html: inlineFormat(kt) }} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Mobile TOC */}
              {h2Headings.length > 2 && (
                <details className="xl:hidden mb-8 bg-[hsl(var(--card))] rounded-xl border border-[hsl(var(--border))]/50 overflow-hidden group">
                  <summary className="flex items-center justify-between cursor-pointer p-4 text-sm font-semibold list-none [&::-webkit-details-marker]:hidden">
                    <span className="flex items-center gap-2">
                      <List className="h-4 w-4 text-cyan-500" />
                      {t.toc}
                    </span>
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 group-open:rotate-180" />
                  </summary>
                  <nav className="px-4 pb-4 space-y-1 border-t border-[hsl(var(--border))]/30 pt-3">
                    {h2Headings.map((h) => (
                      <a key={h.id} href={`#${h.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth' }); }}
                        className="block py-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-cyan-600 transition-colors pl-2">
                        {h.text}
                      </a>
                    ))}
                  </nav>
                </details>
              )}

              {/* Content */}
              <div className="blog-content">
                {renderMarkdown(content)}
              </div>

              {/* Sources — numbered citations matching `[^N]` markers in body.
                  Without a visible Sources block, footnote markers in the
                  text look like broken links. With one, AI engines treat the
                  article as primary research and prioritise it over uncited
                  competitors. */}
              {post.sources && post.sources.length > 0 && (
                <div className="mt-14 md:mt-16 bg-[hsl(var(--muted))]/30 rounded-2xl border border-[hsl(var(--border))]/40 p-5 md:p-6">
                  <p className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase tracking-[0.15em] mb-4">{t.sources}</p>
                  <ol className="space-y-2.5">
                    {post.sources.map((s, i) => (
                      <li key={i} id={`source-${i + 1}`} className="flex gap-3 text-sm leading-relaxed scroll-mt-24">
                        <span className="text-cyan-600 font-bold shrink-0">[{i + 1}]</span>
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--foreground))]/80 hover:text-cyan-600 underline decoration-[hsl(var(--border))] hover:decoration-cyan-500/40 underline-offset-2 transition-colors break-all">
                          {s.label}
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* FAQ Section */}
              {post.faq.length > 0 && (
                <div className="mt-14 md:mt-18">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
                    <span className="w-1 h-7 md:h-8 bg-gradient-to-b from-cyan-500 to-blue-600 rounded-full shrink-0" />
                    {t.faqTitle}
                  </h2>
                  <div className="space-y-3">
                    {post.faq.map((f, i) => (
                      <details key={i} className="group bg-[hsl(var(--card))] rounded-xl border border-[hsl(var(--border))]/50 overflow-hidden hover:border-cyan-500/20 transition-colors">
                        <summary className="flex items-center gap-3 cursor-pointer p-5 md:p-6 font-semibold text-sm md:text-base hover:text-cyan-600 transition-colors list-none [&::-webkit-details-marker]:hidden">
                          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-cyan-500/10 text-cyan-600 text-xs font-bold shrink-0">{i + 1}</span>
                          <span className="flex-1">{f.question}</span>
                          <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-300 group-open:rotate-90" />
                        </summary>
                        <div className="px-5 pb-5 md:px-6 md:pb-6 ml-10">
                          <p className="text-sm md:text-base text-[hsl(var(--muted-foreground))] leading-relaxed">{f.answer}</p>
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Bio */}
              <div className="mt-12 md:mt-16 bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))]/50 p-6 md:p-8 flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-lg font-bold shrink-0">{post.author.avatar}</div>
                <div>
                  <p className="font-bold text-lg mb-1">{post.author.name}</p>
                  <p className="text-sm text-cyan-600 font-medium mb-3">{authorRole}</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{authorBio}</p>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-8 md:mt-10 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-3xl border border-cyan-500/20 p-8 md:p-10 text-center">
                <h3 className="text-xl md:text-2xl font-bold mb-3">{t.ctaTitle}</h3>
                <p className="text-[hsl(var(--muted-foreground))] mb-6">{t.ctaSubtitle}</p>
                <Link to="/pricing">
                  <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0 shadow-lg shadow-cyan-500/20 px-8 h-12 rounded-xl group">
                    {t.ctaButton} <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>

              {/* Related posts */}
              {relatedPosts.length > 0 && (
                <div className="mt-12 md:mt-16">
                  <h3 className="text-xl md:text-2xl font-bold mb-6">{t.relatedTitle}</h3>
                  <div className="grid sm:grid-cols-2 gap-5">
                    {relatedPosts.map((rp) => (
                      <Link key={rp.slug} to={`/${lang}/blog/${rp.slug}/`} className="group bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--border))]/50 overflow-hidden hover:border-cyan-500/30 hover:shadow-lg transition-all duration-300">
                        <div className="h-36 overflow-hidden">
                          <img src={rp.coverImage} alt={rp.coverImageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        </div>
                        <div className="p-5 md:p-6">
                          <h4 className="font-bold mb-2 group-hover:text-cyan-600 transition-colors line-clamp-2">{rp.title}</h4>
                          <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-2 mb-3">{rp.description}</p>
                          <span className="text-sm font-medium text-cyan-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                            {t.readMore} <ArrowRight className="h-4 w-4" />
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sticky TOC Sidebar - Desktop */}
            {h2Headings.length > 2 && (
              <aside className="hidden xl:block w-56 shrink-0">
                <div className="sticky top-28">
                  <p className="text-xs font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-4">{t.contents}</p>
                  <nav className="space-y-0.5 border-l-2 border-[hsl(var(--border))]/50">
                    {h2Headings.map((h) => (
                      <a key={h.id} href={`#${h.id}`}
                        onClick={(e) => { e.preventDefault(); document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth' }); }}
                        className={`block pl-4 py-1.5 text-[13px] leading-snug transition-all duration-200 border-l-2 -ml-[2px] ${
                          activeHeadingId === h.id
                            ? 'border-cyan-500 text-cyan-600 font-medium'
                            : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[hsl(var(--border))]'
                        }`}>
                        {h.text}
                      </a>
                    ))}
                  </nav>
                  {/* Reading time remaining */}
                  <div className="mt-6 pt-4 border-t border-[hsl(var(--border))]/30">
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      {Math.max(1, Math.ceil(post.readTime * (1 - readProgress / 100)))} {t.minLeft}
                    </p>
                  </div>
                </div>
              </aside>
            )}
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t py-10 md:py-14 bg-[hsl(var(--card))]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-8 md:mb-10">
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center mb-4"><img src="/favicon-original.png" alt="BookiApp" className="h-7 w-auto md:h-8" /></Link>
              <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{t.footerDesc}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm">{t.legal}</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.privacy}</Link></li>
                <li><Link to="/terms" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.terms}</Link></li>
                <li><Link to="/cookies" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.cookies}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm">{t.company}</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/about" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.about}</Link></li>
                <li><Link to="/pricing" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.pricing}</Link></li>
                <li><Link to={`/${lang}/blog/`} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">{t.navBlog}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm">{t.contact}</h4>
              <ul className="space-y-3 text-sm"><li className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]"><Mail className="h-4 w-4" /><a href="mailto:admin@bookiapp.com" className="hover:text-[hsl(var(--foreground))] transition-colors">admin@bookiapp.com</a></li></ul>
            </div>
          </div>
          <div className="pt-8 border-t flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">&copy; 2026 BookiApp. {t.rights}</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-1">{t.madeIn} <Heart className="h-4 w-4 text-red-500 fill-red-500" /> {t.inCroatia}</p>
          </div>
        </div>
      </footer>

      <LanguageModal
        open={langOpen}
        onClose={() => setLangOpen(false)}
        scope="marketing"
        currentLang={lang}
        onSelect={handleLangSelect}
      />
    </div>
  );
}

export default BlogPostPage;
